import clsx from 'clsx';
import React from 'react';
import useHeaderTabs, { type Tab } from './useHeaderTabs';

type HeaderTabsProps = Readonly<{
  tabs?: Tab[];
  tabActive?: string;
  onChangeTab?: (id: string) => void;
  onDisableTab?: (id: string) => void;
  typeView?: 'default' | 'vertical';
  align?: 'default' | 'left' | 'right';
  isURLBased?: boolean;
  label?: string;
}>;

function tabElementId(id: string): string {
  return `tab-${id}`;
}

function HeaderTabs({
  tabs = [],
  tabActive,
  onChangeTab,
  onDisableTab = () => {},
  typeView = 'default',
  align = 'default',
  isURLBased = false,
  label,
}: HeaderTabsProps): JSX.Element {
  const { activeTabState, tabRefs, onClickChangeTab, handleKeyDown } = useHeaderTabs({
    tabs,
    tabActive,
    onChangeTab,
    onDisableTab,
    isURLBased,
  });

  function renderTab(tab: Tab, index: number): JSX.Element {
    const isActive = tab.id === activeTabState;
    const className = clsx('header-tabs__tab', `header-tabs__tab--id-${tab.id}`, {
      'header-tabs__tab--active': isActive,
      'header-tabs__tab--disabled': tab.disabled,
    });

    const sharedAriaProps = {
      role: 'tab' as const,
      id: tabElementId(tab.id),
      'aria-selected': isActive,
      'aria-disabled': tab.disabled ? true : undefined,
      tabIndex: tab.disabled ? -1 : 0,
    };

    function handleClick(): void {
      onClickChangeTab(tab);
    }

    function handleKey(event: React.KeyboardEvent<HTMLElement>): void {
      handleKeyDown(event, index);
    }

    const content = (
      <div className="header-tabs__tab-text-container">
        <span className="header-tabs__tab-text">{tab.name}</span>
      </div>
    );

    return (
      <button
        key={tab.id}
        type="button"
        ref={(el: HTMLButtonElement | null) => { tabRefs.current[index] = el; }}
        className={className}
        onClick={handleClick}
        onKeyDown={handleKey}
        {...sharedAriaProps}
      >
        {content}
      </button>
    );
  }

  return (
    <div className={clsx('header-tabs', `header-tabs__type-view-${typeView}`, `header-tabs__align-${align}`)}>
      <div role="tablist" aria-orientation={typeView === 'vertical' ? 'vertical' : 'horizontal'} aria-label={label}>
        {tabs.map((tab, index) => renderTab(tab, index))}
      </div>
    </div>
  );
}

export default HeaderTabs;
